import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, Auth } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, Firestore } from "firebase/firestore";
import { getStorage, connectStorageEmulator, FirebaseStorage } from "firebase/storage";
import { getFirebaseConfig, getFirebaseEmulators } from "../config/firebaseConfig";

export type { FirebaseConfig } from "../config/firebaseConfig";

/**
 * The widget's own, *named* Firebase app.
 *
 * It used to claim the `[DEFAULT]` app at import time. On a host site that
 * also uses Firebase that either stole the host's default slot (their own
 * `initializeApp()` then threw "already exists") or, if the host got there
 * first, silently wrote feedback into the host's project. It also meant the
 * widget's own `firebaseConfig` prop was never applied, since an app already
 * existed by the time the widget rendered. A named app avoids all three.
 */
const APP_NAME = "siren-markup";

export const getMarkupApp = (): FirebaseApp => {
    const existing = getApps().find((app) => app.name === APP_NAME);
    if (existing) return existing;

    const app = initializeApp(getFirebaseConfig(), APP_NAME);
    // Emulators have to be connected before first use, so it happens here,
    // once, as the app is created.
    const emulators = getFirebaseEmulators();
    if (emulators) {
        const host = emulators.host ?? "127.0.0.1";
        if (emulators.auth) {
            connectAuthEmulator(getAuth(app), `http://${host}:${emulators.auth}`, {
                disableWarnings: true,
            });
        }
        if (emulators.firestore) {
            connectFirestoreEmulator(getFirestore(app), host, emulators.firestore);
        }
        if (emulators.storage) {
            connectStorageEmulator(getStorage(app), host, emulators.storage);
        }
    }
    return app;
};

export const getFirebaseAuth = (): Auth => getAuth(getMarkupApp());
export const getFirebaseDb = (): Firestore => getFirestore(getMarkupApp());
export const getFirebaseStorage = (): FirebaseStorage =>
    getStorage(getMarkupApp());
